import * as React from 'react';
import { Stack, Link, Text } from '@withjoy/joykit';
import { EnhancedStack, InputStack, FullWidthButton, CenterText, ErrorText } from './styles';
import { buildReactStateHandler } from '@lib';
import { Input } from '..';
import { CurrentForm } from '../..';
import { utils } from '../../utils';
import { InputField } from '@routes/event/create/CreateWedding/types';
import { authService, Analytics } from '@withjoy/web-core';

interface ForgotPasswordProps {
  handleFormChange: (currentForm: CurrentForm) => void;
  forgotPasswordAnalytics: Analytics;
}

interface ForgotPasswordState {
  email: InputField;
  emailFocused: boolean;
  responseError: undefined | Error;
}

const stateHandler = buildReactStateHandler<ForgotPasswordState>();

export class ForgotPassword extends React.Component<ForgotPasswordProps, ForgotPasswordState> {
  readonly state: ForgotPasswordState = {
    emailFocused: true,
    email: {
      value: '',
      error: false,
      errorText: '',
      label: 'Email',
      placeholder: 'Enter your email',
      name: 'email'
    },
    responseError: undefined
  };

  handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    this.setState(
      stateHandler(draft => {
        draft.email.value = value;
        draft.responseError = undefined;
        if (draft.email.error) {
          draft.email.error = false;
        }
      })
    );
  };

  handleFocus = (focused: boolean) => {
    this.setState(
      stateHandler(draft => {
        draft.emailFocused = focused;
      })
    );
  };

  handleBlur = (focused: boolean) => {
    this.setState(
      stateHandler(draft => {
        draft.emailFocused = focused;
      })
    );
  };

  canSubmit = (field: InputField) => {
    return utils.isValidEmail(field.value);
  };

  handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const field = this.state.email;
    if (this.canSubmit(field)) {
      this.props.forgotPasswordAnalytics.track({
        action: 'ResetPassword',
        actionType: 'click',
        label: 'Reset Password'
      });
      try {
        const res = await authService.changePassword(field.value);
        this.setState(
          stateHandler(draft => {
            draft.email.value = '';
            draft.email.error = false;
          })
        );
        this.props.handleFormChange('login');
        return res;
      } catch (e) {
        this.setState(
          stateHandler(draft => {
            draft.email.value = '';
            draft.email.error = true;
            draft.responseError = e.message;
          }),
          () => {
            this.props.forgotPasswordAnalytics.trackError({
              attemptedAction: 'forgot password',
              error: 'CreateWeddingValidation',
              errorDescription: e.message
            });
          }
        );
        return;
      }
    } else {
      const errorText = utils.isEmpty(field) ? 'Please enter an email' : 'Email invalid';

      this.setState(
        stateHandler(draft => {
          draft.email.error = true;
          draft.email.errorText = errorText;
        }),
        () => {
          this.props.forgotPasswordAnalytics.trackError({
            attemptedAction: 'forgot password',
            error: 'CreateWeddingValidation',
            errorDescription: errorText
          });
        }
      );
      return;
    }
  };

  render() {
    const { email, responseError } = this.state;

    return (
      <EnhancedStack spacing="md">
        <Text component="h1" variant="page">
          Forgot your password?
        </Text>
        <CenterText color="#A9A9A9" component="p" variant="body">
          Enter the email you used to signup for Joy and we’ll send you an email to reset it.
        </CenterText>
        <form style={{ width: '100%', marginBottom: '40px', marginTop: '8px' }} onSubmit={this.handleSubmit}>
          <InputStack spacing="sm">
            <Input
              onChange={this.handleOnChange}
              value={email.value}
              handleFocus={this.handleFocus}
              handleBlur={this.handleBlur}
              iconType="mail"
              placeholder={email.placeholder}
              dataInput={email.name}
              type="email"
              name={email.name}
              label={email.label}
              error={email.error}
              errorText={email.errorText}
            />
          </InputStack>
          {responseError && (
            <ErrorText color="#DA505D" component="p" variant="caption">
              {responseError.message}
            </ErrorText>
          )}
          <FullWidthButton type="submit">Reset Password</FullWidthButton>
        </form>
        <Stack spacing="md">
          <Text color="#a9a9a9" component="p" variant="body">
            Already have an account? <Link onClick={() => this.props.handleFormChange('login')}>Log in</Link>
          </Text>
          <Text color="#a9a9a9" component="p" variant="body">
            New to Joy? <Link onClick={() => this.props.handleFormChange('signup')}>Create An Account</Link>
          </Text>
        </Stack>
      </EnhancedStack>
    );
  }
}
